'use client';

import Image from 'next/image';
import Link from 'next/link';
import { motion } from 'framer-motion';
import {
  Paintbrush,
  Globe,
  Layers,
  BarChart3,
  Code,
  Palette,
  Lock,
  Server,
  ArrowRight,
  Sparkles,
  Zap,
  Users,
  Building2,
  TrendingUp,
  Shield,
  Clock,
  DollarSign,
  Rocket,
  Settings,
  Smartphone,
  FileText,
  Database,
  Webhook,
  CreditCard,
  Globe2,
  Route,
  Activity,
} from 'lucide-react';
import { PAGE_HERO_IMAGES } from '@/lib/marketing-images';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

const staggerContainer = {
  initial: { opacity: 0 },
  whileInView: { opacity: 1 },
  viewport: { once: true },
  transition: { staggerChildren: 0.1 },
};

export default function WhiteLabelPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* ══════════════════════════════════════════════════════
          HERO SECTION
          ══════════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden gradient-hero noise-overlay grid-bg pt-24 pb-20 lg:pt-32 lg:pb-28">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[600px] w-[600px] rounded-full opacity-20 blur-[120px]" style={{ background: 'radial-gradient(circle, rgba(168,85,247,0.4), transparent 70%)' }} />
        <div className="absolute bottom-0 right-0 h-[400px] w-[400px] rounded-full opacity-15 blur-[100px]" style={{ background: 'radial-gradient(circle, rgba(0,212,255,0.3), transparent 70%)' }} />

        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center">
            <span className="inline-flex items-center gap-2 rounded-full glass-card px-4 py-1.5 text-xs font-medium text-neon-purple mb-6">
              <Paintbrush className="h-3 w-3" />
              White-Label Telematics Platform
            </span>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="text-4xl font-bold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl xl:text-7xl mb-6"
            >
              Launch Your Own
              <br />
              <span className="text-gradient">Telematics Brand</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mx-auto max-w-3xl text-lg leading-relaxed text-slate-300 mb-8"
            >
              Deploy a fully branded telematics platform under your domain, logo, and identity. Zero development required. Go to market in weeks, not years.
            </motion.p>

            <div className="mx-auto mb-8 max-w-4xl">
              <div className="glass-card-heavy rounded-3xl p-2 border border-white/10">
                <Image
                  src={PAGE_HERO_IMAGES.whiteLabel.src}
                  alt={PAGE_HERO_IMAGES.whiteLabel.alt}
                  width={1600}
                  height={900}
                  className="h-auto w-full rounded-2xl object-cover"
                  priority
                />
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col items-center justify-center gap-4 sm:flex-row"
            >
              <Link href="/contact">
                <button className="btn-neon rounded-full px-8 py-3.5 text-sm font-semibold flex items-center gap-2 shadow-lg">
                  Start Your White-Label Journey
                  <ArrowRight className="h-4 w-4" />
                </button>
              </Link>
              <Link href="#demo">
                <button className="btn-neon-outline rounded-full px-8 py-3.5 text-sm font-semibold flex items-center gap-2">
                  Book a Demo
                  <Sparkles className="h-4 w-4" />
                </button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          WHAT IS WHITE-LABELING?
          ══════════════════════════════════════════════════════ */}
      <section className="gradient-section-purple py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white sm:text-4xl lg:text-5xl mb-4">
              What is <span className="text-gradient-warm">White-Labeling?</span>
            </h2>
            <p className="max-w-3xl mx-auto text-slate-300 text-lg leading-relaxed">
              You get the full GlonexTrack platform rebranded as your own. Your clients never see GlonexTrack. It&apos;s your platform, your brand, your business.
            </p>
          </motion.div>

          <motion.div {...fadeInUp} className="max-w-4xl mx-auto">
            <div className="glass-card-heavy rounded-2xl p-8 lg:p-12">
              <div className="grid gap-8 md:grid-cols-2">
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-neon-purple/10 text-neon-purple flex-shrink-0">
                      <Globe className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-2">Your Domain, Your Brand</h3>
                      <p className="text-slate-300">
                        Your clients access the platform at yourdomain.com. They see your logo, your colors, your brand identity throughout the entire experience.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-neon-cyan/10 text-neon-cyan flex-shrink-0">
                      <Lock className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-2">Complete Isolation</h3>
                      <p className="text-slate-300">
                        Each client&apos;s data is completely separated. Multi-tenant architecture ensures privacy, security, and compliance with data residency requirements.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-neon-purple/10 text-neon-purple flex-shrink-0">
                      <DollarSign className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-2">Your Pricing, Your Margin</h3>
                      <p className="text-slate-300">
                        Set your own pricing tiers, margins, and billing cycles. You own the customer relationship and revenue model.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-neon-cyan/10 text-neon-cyan flex-shrink-0">
                      <Rocket className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-2">Weeks, Not Years</h3>
                      <p className="text-slate-300">
                        No development team needed. Configure your brand, choose features, and launch. We handle all infrastructure, updates, and maintenance.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          KEY BENEFITS GRID
          ══════════════════════════════════════════════════════ */}
      <section className="gradient-section py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white sm:text-4xl lg:text-5xl mb-4">
              Everything You Need to <span className="text-gradient">Build Your Brand</span>
            </h2>
            <p className="max-w-2xl mx-auto text-slate-300 text-lg">
              A complete white-label solution with all the tools to launch and scale your telematics business.
            </p>
          </motion.div>

          <motion.div {...staggerContainer} className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: Globe2,
                title: 'Custom Domain & SSL',
                desc: 'Deploy at yourplatform.com with full SSL certificate management. Your clients never see GlonexTrack branding.',
                color: 'text-neon-purple',
                bg: 'bg-neon-purple/10',
                border: 'neon-border-purple',
              },
              {
                icon: Palette,
                title: 'Brand Identity',
                desc: 'Your logo, colors, favicon, and email templates. Complete visual customization across web, mobile, and communications.',
                color: 'text-neon-purple',
                bg: 'bg-neon-purple/10',
                border: 'neon-border-purple',
              },
              {
                icon: FileText,
                title: 'CMS Landing Pages',
                desc: 'Build your own marketing pages with drag-and-drop CMS. Create custom landing pages, pricing pages, and documentation.',
                color: 'text-neon-cyan',
                bg: 'bg-neon-cyan/10',
                border: 'neon-border-cyan',
              },
              {
                icon: Smartphone,
                title: 'Branded Mobile App',
                desc: 'iOS & Android apps published under your brand name in App Store and Google Play. Full white-label mobile experience.',
                color: 'text-neon-cyan',
                bg: 'bg-neon-cyan/10',
                border: 'neon-border-cyan',
              },
              {
                icon: Lock,
                title: 'Isolated Multi-Tenancy',
                desc: 'Each client\'s data is completely separated. Enterprise-grade security with data residency options and compliance controls.',
                color: 'text-neon-purple',
                bg: 'bg-neon-purple/10',
                border: 'neon-border-purple',
              },
              {
                icon: BarChart3,
                title: 'Revenue Dashboard',
                desc: 'Track MRR, churn, device activations, and client health metrics. Monitor your white-label business performance in real-time.',
                color: 'text-neon-purple',
                bg: 'bg-neon-purple/10',
                border: 'neon-border-purple',
              },
              {
                icon: Code,
                title: 'Full API Access',
                desc: 'Build custom integrations with our REST & WebSocket APIs. Webhook system for real-time event notifications and data sync.',
                color: 'text-neon-cyan',
                bg: 'bg-neon-cyan/10',
                border: 'neon-border-cyan',
              },
              {
                icon: CreditCard,
                title: 'Custom Pricing',
                desc: 'Set your own pricing tiers, margins, and billing cycles. Flexible billing models with support for subscriptions and usage-based pricing.',
                color: 'text-neon-cyan',
                bg: 'bg-neon-cyan/10',
                border: 'neon-border-cyan',
              },
            ].map((benefit, i) => (
              <motion.div
                key={i}
                {...fadeInUp}
                transition={{ delay: i * 0.05 }}
                className={`glass-card rounded-xl p-6 hover:glass-card-heavy transition-all duration-300 ${benefit.border}`}
              >
                <div className={`mb-4 inline-flex rounded-lg ${benefit.bg} p-3 ${benefit.color}`}>
                  <benefit.icon className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{benefit.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          HOW IT WORKS
          ══════════════════════════════════════════════════════ */}
      <section className="gradient-section-purple py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white sm:text-4xl lg:text-5xl mb-4">
              How It <span className="text-gradient-warm">Works</span>
            </h2>
            <p className="max-w-2xl mx-auto text-slate-300 text-lg">
              Launch your white-label platform in three simple steps. No technical expertise required.
            </p>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-3">
            {[
              {
                step: '01',
                title: 'Configure Your Brand',
                desc: 'Upload your logo, set brand colors, configure domain, and customize email templates. Our team helps you get everything pixel-perfect.',
                icon: Settings,
                color: 'text-neon-purple',
                bg: 'bg-neon-purple/10',
              },
              {
                step: '02',
                title: 'Choose Features',
                desc: 'Select which modules to enable: GPS tracking, cameras, AI analytics, reports, geofencing, and more. Enable only what you need.',
                icon: Layers,
                color: 'text-neon-cyan',
                bg: 'bg-neon-cyan/10',
              },
              {
                step: '03',
                title: 'Launch & Grow',
                desc: 'Onboard your clients, track revenue in your dashboard, and scale. We handle infrastructure, updates, and 24/7 support.',
                icon: Rocket,
                color: 'text-neon-purple',
                bg: 'bg-neon-purple/10',
              },
            ].map((step, i) => (
              <motion.div
                key={i}
                {...fadeInUp}
                transition={{ delay: i * 0.15 }}
                className="glass-card-heavy rounded-2xl p-8 text-center"
              >
                <div className={`mb-6 inline-flex rounded-2xl ${step.bg} p-4 ${step.color}`}>
                  <step.icon className="h-8 w-8" />
                </div>
                <div className="mb-4 inline-flex items-center justify-center rounded-full bg-neon-purple/10 px-4 py-1 text-xs font-semibold text-neon-purple">
                  Step {step.step}
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">{step.title}</h3>
                <p className="text-slate-300 leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          WHO IS IT FOR
          ══════════════════════════════════════════════════════ */}
      <section className="gradient-section py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white sm:text-4xl lg:text-5xl mb-4">
              Who is White-Label <span className="text-gradient">For?</span>
            </h2>
            <p className="max-w-2xl mx-auto text-slate-300 text-lg">
              Perfect for companies looking to offer telematics under their own brand without building from scratch.
            </p>
          </motion.div>

          <motion.div {...staggerContainer} className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: Route,
                title: 'Tracking Companies',
                desc: 'Expand your service offering with a complete telematics platform under your established brand.',
                color: 'text-neon-purple',
                bg: 'bg-neon-purple/10',
              },
              {
                icon: Shield,
                title: 'Insurance Tech Providers',
                desc: 'Offer UBI and telematics solutions to insurance partners with your own branded platform.',
                color: 'text-neon-purple',
                bg: 'bg-neon-purple/10',
              },
              {
                icon: Building2,
                title: 'Fleet Management Resellers',
                desc: 'Resell enterprise fleet management solutions with your brand, pricing, and customer relationships.',
                color: 'text-neon-cyan',
                bg: 'bg-neon-cyan/10',
              },
              {
                icon: Activity,
                title: 'IoT Companies',
                desc: 'Add telematics capabilities to your IoT platform without building complex infrastructure.',
                color: 'text-neon-cyan',
                bg: 'bg-neon-cyan/10',
              },
              {
                icon: Server,
                title: 'Telecoms',
                desc: 'Bundle telematics services with your connectivity offerings for enterprise customers.',
                color: 'text-neon-purple',
                bg: 'bg-neon-purple/10',
              },
              {
                icon: Settings,
                title: 'System Integrators',
                desc: 'Integrate telematics into your existing solutions and offer it as part of your service portfolio.',
                color: 'text-neon-cyan',
                bg: 'bg-neon-cyan/10',
              },
            ].map((audience, i) => (
              <motion.div
                key={i}
                {...fadeInUp}
                transition={{ delay: i * 0.05 }}
                className="glass-card rounded-xl p-6 hover:glass-card-heavy transition-all duration-300"
              >
                <div className={`mb-4 inline-flex rounded-lg ${audience.bg} p-3 ${audience.color}`}>
                  <audience.icon className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{audience.title}</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{audience.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          REVENUE MODEL
          ══════════════════════════════════════════════════════ */}
      <section className="gradient-section-purple py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white sm:text-4xl lg:text-5xl mb-4">
              Your Revenue, <span className="text-gradient-warm">Your Control</span>
            </h2>
            <p className="max-w-2xl mx-auto text-slate-300 text-lg">
              Set your own prices, keep the margin. We provide the infrastructure, you own the relationship.
            </p>
          </motion.div>

          <motion.div {...fadeInUp} className="max-w-5xl mx-auto">
            <div className="glass-card-heavy rounded-2xl p-8 lg:p-12">
              <div className="grid gap-8 md:grid-cols-2">
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-neon-purple/10 text-neon-purple flex-shrink-0">
                      <DollarSign className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-2">Set Your Own Pricing</h3>
                      <p className="text-slate-300">
                        Charge what the market will bear. Create custom pricing tiers, volume discounts, and promotional offers. You control the revenue model.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-neon-cyan/10 text-neon-cyan flex-shrink-0">
                      <TrendingUp className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-2">Keep the Margin</h3>
                      <p className="text-slate-300">
                        White-label pricing is based on usage and features. The difference between what you charge and what you pay is your profit margin.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-neon-purple/10 text-neon-purple flex-shrink-0">
                      <Users className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-2">Own the Relationship</h3>
                      <p className="text-slate-300">
                        Your clients interact with your brand, your support team, and your billing. You maintain full control over customer relationships.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-neon-cyan/10 text-neon-cyan flex-shrink-0">
                      <BarChart3 className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-2">Track Performance</h3>
                      <p className="text-slate-300">
                        Monitor MRR, churn, device activations, and revenue metrics in your dedicated white-label dashboard. Data-driven growth insights.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          TECHNICAL SPECIFICATIONS
          ══════════════════════════════════════════════════════ */}
      <section className="gradient-section py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white sm:text-4xl lg:text-5xl mb-4">
              Enterprise-Grade <span className="text-gradient">Infrastructure</span>
            </h2>
            <p className="max-w-2xl mx-auto text-slate-300 text-lg">
              Built on scalable, secure infrastructure designed for mission-critical telematics operations.
            </p>
          </motion.div>

          <motion.div {...staggerContainer} className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: Activity,
                title: '99.9% Uptime SLA',
                desc: 'Enterprise-grade reliability with guaranteed uptime and redundancy across multiple data centers.',
                color: 'text-neon-purple',
                bg: 'bg-neon-purple/10',
              },
              {
                icon: Globe,
                title: 'Data Residency Options',
                desc: 'Choose data storage location to meet compliance requirements. Support for regional data residency.',
                color: 'text-neon-purple',
                bg: 'bg-neon-purple/10',
              },
              {
                icon: Zap,
                title: 'High API Rate Limits',
                desc: 'REST API: 10,000 requests/minute. WebSocket: Real-time bidirectional communication with no rate limits.',
                color: 'text-neon-cyan',
                bg: 'bg-neon-cyan/10',
              },
              {
                icon: Shield,
                title: 'SSO / SAML Support',
                desc: 'Enterprise authentication with SAML 2.0, OAuth 2.0, and custom SSO integration for seamless access.',
                color: 'text-neon-cyan',
                bg: 'bg-neon-cyan/10',
              },
              {
                icon: Webhook,
                title: 'Webhook System',
                desc: 'Real-time event notifications via webhooks. Configure custom endpoints for telemetry, alerts, and system events.',
                color: 'text-neon-purple',
                bg: 'bg-neon-purple/10',
              },
              {
                icon: Database,
                title: 'Scalable Architecture',
                desc: 'Auto-scaling infrastructure handles millions of devices and billions of data points without performance degradation.',
                color: 'text-neon-purple',
                bg: 'bg-neon-purple/10',
              },
              {
                icon: Lock,
                title: 'End-to-End Encryption',
                desc: 'Data encrypted in transit and at rest. SOC 2 Type II compliant with regular security audits.',
                color: 'text-neon-cyan',
                bg: 'bg-neon-cyan/10',
              },
              {
                icon: Clock,
                title: '24/7 Support',
                desc: 'Dedicated white-label support team available around the clock. Priority support for critical issues.',
                color: 'text-neon-cyan',
                bg: 'bg-neon-cyan/10',
              },
            ].map((spec, i) => (
              <motion.div
                key={i}
                {...fadeInUp}
                transition={{ delay: i * 0.05 }}
                className="glass-card rounded-xl p-6 hover:glass-card-heavy transition-all duration-300"
              >
                <div className={`mb-4 inline-flex rounded-lg ${spec.bg} p-3 ${spec.color}`}>
                  <spec.icon className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{spec.title}</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{spec.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          COMPARISON TABLE
          ══════════════════════════════════════════════════════ */}
      <section className="gradient-section-purple py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white sm:text-4xl lg:text-5xl mb-4">
              DIY vs <span className="text-gradient-warm">GlonexTrack White-Label</span>
            </h2>
            <p className="max-w-2xl mx-auto text-slate-300 text-lg">
              See why building from scratch doesn&apos;t make sense when you can launch in weeks.
            </p>
          </motion.div>

          <motion.div {...fadeInUp} className="overflow-x-auto">
            <div className="glass-card-heavy rounded-2xl p-8 min-w-full">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-4 px-4 text-sm font-semibold text-slate-300 uppercase tracking-wider">Feature</th>
                    <th className="text-center py-4 px-4 text-sm font-semibold text-slate-300 uppercase tracking-wider">DIY Development</th>
                    <th className="text-center py-4 px-4 text-sm font-semibold text-neon-purple uppercase tracking-wider">GlonexTrack White-Label</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {[
                    { feature: 'Development Time', diy: '2-3 years', whiteLabel: '2-4 weeks' },
                    { feature: 'Initial Cost', diy: '$500K - $2M+', whiteLabel: 'Setup fee + monthly' },
                    { feature: 'Time to Market', diy: '24-36 months', whiteLabel: '4-8 weeks' },
                    { feature: 'Infrastructure Maintenance', diy: 'Ongoing DevOps team', whiteLabel: 'Handled by us' },
                    { feature: 'Feature Updates', diy: 'Build yourself', whiteLabel: 'Automatic updates' },
                    { feature: 'Security & Compliance', diy: 'Your responsibility', whiteLabel: 'SOC 2 Type II certified' },
                    { feature: 'Mobile Apps', diy: 'Separate development', whiteLabel: 'Included & branded' },
                    { feature: 'Support & Maintenance', diy: 'Your team 24/7', whiteLabel: 'Dedicated support team' },
                  ].map((row, i) => (
                    <tr key={i} className="hover:bg-white/8 transition-colors">
                      <td className="py-4 px-4 text-white font-medium">{row.feature}</td>
                      <td className="py-4 px-4 text-center text-slate-300">{row.diy}</td>
                      <td className="py-4 px-4 text-center text-neon-purple font-semibold">{row.whiteLabel}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          CTA SECTION
          ══════════════════════════════════════════════════════ */}
      <section className="gradient-hero relative overflow-hidden py-20 lg:py-28">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] rounded-full opacity-20 blur-[100px]" style={{ background: 'radial-gradient(circle, rgba(168,85,247,0.4), transparent 70%)' }} />
        
        <div className="relative z-10 mx-auto max-w-4xl px-6 lg:px-8 text-center">
          <motion.div {...fadeInUp}>
            <div className="mb-6 inline-flex items-center gap-2 rounded-full glass-card px-4 py-1.5 text-xs font-medium text-neon-purple">
              <Rocket className="h-3 w-3" />
              Ready to Launch?
            </div>
            <h2 className="text-3xl font-bold text-white sm:text-4xl lg:text-5xl mb-6">
              Start Your White-Label Journey Today
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-slate-300 mb-8">
              Join companies that have launched their telematics brands in weeks, not years. Book a demo to see the platform in action.
            </p>
            <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link href="/contact">
                <button className="btn-neon rounded-full px-8 py-3.5 text-sm font-semibold flex items-center gap-2 shadow-lg">
                  Get Started
                  <ArrowRight className="h-4 w-4" />
                </button>
              </Link>
              <Link href="#demo">
                <button className="btn-neon-outline rounded-full px-8 py-3.5 text-sm font-semibold flex items-center gap-2">
                  Schedule a Demo
                  <Sparkles className="h-4 w-4" />
                </button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
